import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useSignUp, useSignIn } from '@clerk/clerk-react';
import { Shield, CheckCircle, Mail, Eye, FileText, Bell, Clock, Star, ArrowRight, ArrowLeft, User, Lock, Zap } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Separator } from '../ui/Separator';
import { Badge } from '../ui/Badge';
import { useAnalytics } from '../../hooks/useAnalytics';

interface CustomAuthFormProps {
  mode: 'signin' | 'signup';
}

const CustomAuthForm: React.FC<CustomAuthFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { isLoaded: signUpLoaded, signUp, setActive } = useSignUp();
  const { isLoaded: signInLoaded, signIn } = useSignIn();
  const { trackAuthEvent, trackEvent, trackSignUpConversion } = useAnalytics();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already signed in
  if (authLoaded && isSignedIn) {
    navigate('/');
    return null;
  }

  // Show loading while Clerk is initializing
  if (!authLoaded || !signUpLoaded || !signInLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const handleGoogleAuth = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Track Google auth attempt
      trackAuthEvent(mode === 'signup' ? 'sign_up' : 'sign_in', 'google');
      
      if (mode === 'signup') {
        await signUp?.authenticateWithRedirect({
          strategy: 'oauth_google',
          redirectUrl: window.location.origin + '/',
          redirectUrlComplete: window.location.origin + '/',
        });
      } else {
        await signIn?.authenticateWithRedirect({
          strategy: 'oauth_google',
          redirectUrl: window.location.origin + '/',
          redirectUrlComplete: window.location.origin + '/',
        });
      }
    } catch (err: any) {
      console.error('Error with Google auth:', err);
      setError(`Failed to ${mode === 'signup' ? 'sign up' : 'sign in'} with Google. Please try again.`);
      setIsSubmitting(false);
      
      // Track auth error
      trackEvent('Authentication', 'error', 'google_auth_failed');
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp || !signIn) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (mode === 'signup') {
        // Track sign up attempt
        trackAuthEvent('sign_up', 'email');
        
        // Create the user account
        const result = await signUp.create({
          emailAddress: email,
          password,
        });

        console.log('Sign up created:', result);

        // Send verification email with code strategy
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setPendingVerification(true);
      } else {
        // Track sign in attempt
        trackAuthEvent('sign_in', 'email');
        
        // Sign in
        const result = await signIn.create({
          identifier: email,
          password,
        });

        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          console.log('Sign in successful');
          navigate('/');
        } else {
          setError('Sign in failed. Please check your credentials.');
        }
      }
    } catch (err: any) {
      console.error(`Error during ${mode}:`, err);
      
      // Handle specific Clerk errors
      if (err.errors && err.errors.length > 0) {
        const errorMessage = err.errors[0].message;
        if (errorMessage.includes('email_address')) {
          setError('This email address is already registered. Please use a different email or try signing in.');
        } else if (errorMessage.includes('password')) {
          setError('Password must be at least 8 characters long and contain a mix of letters and numbers.');
        } else {
          setError(errorMessage);
        }
      } else {
        setError(`Failed to ${mode === 'signup' ? 'create account' : 'sign in'}. Please check your information and try again.`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const completeSignUp = await signUp.attemptEmailAddressVerification({
        code,
      });

      console.log('Verification result:', completeSignUp);

      if (completeSignUp.status === 'complete') {
        await setActive({ session: completeSignUp.createdSessionId });
        console.log('Account verified and session created successfully');
        
        // Track successful verification
        trackAuthEvent('sign_up', 'email_verified');
        trackEvent('Authentication', 'verification_success', 'email');
        
        // Track sign-up conversion for Google Ads
        trackSignUpConversion();
        
        navigate('/');
      } else {
        setError('Verification incomplete. Please check the code and try again.');
      }
    } catch (err: any) {
      console.error('Error during verification:', err);
      
      // Track verification error
      trackEvent('Authentication', 'verification_error', 'email');
      
      // Handle specific verification errors
      if (err.errors && err.errors.length > 0) {
        const errorMessage = err.errors[0].message;
        if (errorMessage.includes('code')) {
          setError('Invalid verification code. Please check your email and try again.');
        } else {
          setError(errorMessage);
        }
      } else {
        setError('Verification failed. Please check the code and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <FileText className="w-5 h-5" />,
      title: "File Management",
      description: "Upload, organize, and manage files for all your reviews"
    },
    {
      icon: <Bell className="w-5 h-5" />,
      title: "Automated Reminders",
      description: "Set up smart reminders to keep reviews on track"
    },
    {
      icon: <Star className="w-5 h-5" />,
      title: "Review History",
      description: "Keep track of all your past reviews and feedback"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: "Increased Limits",
      description: "Create up to 5 reviews daily instead of 2"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left half: Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="space-y-8">
            {/* Header */}
            <div className="text-center">
              <img 
                src="/approvia-logo.png" 
                alt="Approvia" 
                className="h-16 w-auto object-contain mx-auto mb-6"
              />
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Welcome to Approvia
              </h1>
              <p className="text-xl text-gray-600">
                {mode === 'signin' 
                  ? 'Sign in to your account to continue your journey.'
                  : 'Create your account and start getting to a "Yes" faster.'
                }
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Email Verification Form */}
            {pendingVerification && mode === 'signup' ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Verify Your Email
                  </CardTitle>
                  <CardDescription>
                    We've sent a verification code to {email}. Please check your email and enter the code below.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleVerifyEmail} className="space-y-4">
                    <div>
                      <Label htmlFor="verification-code">Verification Code</Label>
                      <Input
                        id="verification-code"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? 'Verifying...' : 'Verify Email'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              /* Main Auth Form */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    {mode === 'signup' ? 'Create Account' : 'Sign In'}
                  </CardTitle>
                  <CardDescription>
                    {mode === 'signup' 
                      ? 'Join thousands of professionals getting faster approvals'
                      : 'Welcome back! Sign in to continue'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleEmailAuth} className="space-y-4">
                    {mode === 'signup' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="first-name">First Name</Label>
                          <Input
                            id="first-name"
                            type="text"
                            placeholder="John"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="last-name">Last Name</Label>
                          <Input
                            id="last-name"
                            type="text"
                            placeholder="Doe"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? (mode === 'signup' ? 'Creating Account...' : 'Signing In...') : (mode === 'signup' ? 'Create Account' : 'Sign In')}
                    </Button>
                  </form>
                  
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full" 
                    onClick={handleGoogleAuth}
                    disabled={isSubmitting}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {mode === 'signup' ? 'Sign up with Google' : 'Sign in with Google'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Benefits Section - Only show for signup */}
            {mode === 'signup' && !pendingVerification && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center">Why choose Approvia?</h3>
                <div className="grid gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-primary mt-0.5">
                        {benefit.icon}
                      </div>
                      <div>
                        <h4 className="font-medium text-sm">{benefit.title}</h4>
                        <p className="text-xs text-muted-foreground">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mode Toggle */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {mode === 'signin' ? "Don't have an account? " : "Already have an account? "}
                <button
                  onClick={() => navigate(mode === 'signin' ? '/auth?mode=signup' : '/auth?mode=signin')}
                  className="text-primary hover:underline font-medium"
                >
                  {mode === 'signin' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right half: Background Image - Hidden on mobile */}
      <div className="hidden md:block w-1/2 bg-cover bg-center bg-no-repeat rounded-3xl shadow-2xl m-8" 
           style={{ backgroundImage: `url(https://images.unsplash.com/photo-1642615835477-d303d7dc9ee9?w=2160&q=80)` }}>
      </div>
    </div>
  );
};

export default CustomAuthForm; 